import { VisualisationContextProps } from 'widgets/supportVisualisation/Content/VisualisationContentProps';
import React, { useState } from 'react';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Grid } from '@instinet-analytics/semantic-ui-components';
import './Requests.scss';

import { ApiRequestInfo } from 'communication/ApiCallInfo';
import RequestFilters, {
    applyFilter,
    getChannels,
    RequestFilter,
} from 'widgets/supportVisualisation/Content/Requests/RequestFilters';
import { RequestProgressState } from 'requestProgress/store/RequestProgressState';
import { ContactSupportRequestState } from 'communication/types/ContactSupportRequest';
import orderBy from 'lodash/orderBy';
import RequestPanel from 'widgets/supportVisualisation/Content/Requests/RequestPanel';
import { toDateString } from 'widgets/supportVisualisation/SupportVisualisationHelpers';
import { extractLogMessagesFromLogs } from 'logging/store/LogMessagesStoreService';
import { PostingMessage } from 'communication/streaming/SockJSStreamingService';

export interface RequestInfo extends ApiRequestInfo {
    key: string;
    verbInfo: string;
    isLatest: boolean;
}

export default function Requests(props: VisualisationContextProps) {
    const { state, height, width } = props;

    const [selectedRequest, setSelectedRequest] = useState<
        RequestInfo | undefined
    >(undefined);

    const [filter, setFilter] = useState<RequestFilter>({
        keys: [],
        onlyLatestRequests: false,
        includeRpcChannels: true,
    });

    const requests: RequestInfo[] = [];
    extractRequests(requests, state?.state?.requestProgress);
    extractRpcChannels(requests, filter, state);
    extractBatchMessages(requests, state);

    if (requests.length === 0) {
        return <div>None found in state</div>;
    }

    const orderedRequests = orderBy(requests, ['sentTime'], ['desc']);
    calcIsLatest(orderedRequests);
    const filteredRequests = applyFilter(orderedRequests, filter);
    for (const f of filteredRequests) {
        if (f.response) {
            (f.response as unknown as { dataLength: number }).dataLength =
                JSON.stringify(f.response.data ?? '').length;
        }
    }

    const rowData = filteredRequests;

    const columnDefs = getColumns();

    const onSelectedRowIdsChanged = (rowDataIds: string[]) => {
        if (rowDataIds.length !== 1) {
            setSelectedRequest(undefined);
        }
        const row = orderedRequests.find((r) => r.id === rowDataIds[0]);
        setSelectedRequest(row);
    };

    const panelHeight = height / 2;

    const onUpdateFilter = (nextFilter: RequestFilter) => {
        setFilter(nextFilter);
    };

    return (
        <div className={'Requests'}>
            <RequestFilters
                filter={filter}
                onUpdateFilter={onUpdateFilter}
                state={state!}
            />
            <Grid
                autoSizeColumns={true}
                singleSelectOnly={true}
                columnDefs={columnDefs}
                rowData={rowData}
                getRowDataId={(r) => r.id}
                onSelectedRowIdsChanged={onSelectedRowIdsChanged}
            />
            {selectedRequest && (
                <RequestPanel
                    request={selectedRequest}
                    height={panelHeight}
                    width={width}
                />
            )}
        </div>
    );
}

function getColumns(): ColDef[] {
    return [
        {
            field: 'sentTime',
            headerName: 'Time',
            resizable: true,
            width: 80,
            cellRenderer: (props: { value: Date }) => {
                return <span>{toDateString(props.value)}</span>;
            },
        },
        {
            field: 'key',
            headerName: 'Key',
            width: 100,
            resizable: true,
        },
        { field: 'verbInfo', headerName: 'Type', width: 60, sortable: true },

        {
            field: 'url',
            headerName: 'Url',
            resizable: true,
            width: 250,
        },

        {
            field: 'response.status',
            headerName: 'Response',
            resizable: true,
            width: 70,
        },
        {
            field: 'response.dataLength',
            headerName: 'Response Length',
            resizable: true,
            width: 140,
        },
    ];
}

function extractRequests(
    requests: RequestInfo[],
    requestState?: RequestProgressState
) {
    if (!requestState) {
        return;
    }
    for (const key of Object.keys(requestState)) {
        const requestProgress = requestState[key];
        for (const request of requestProgress.requests ?? []) {
            requests.push({
                key,
                verbInfo: request.verb,
                ...request,
                isLatest: false,
                ...{ id: (requests.length + 1).toString() },
            });
        }
    }
}

function extractBatchMessages(
    requests: RequestInfo[],
    state?: ContactSupportRequestState
) {
    if (!state) {
        return;
    }
    const messages = extractLogMessagesFromLogs(state.logMessages).filter((m) =>
        m.message.startsWith(PostingMessage)
    );

    for (const message of messages) {
        requests.push({
            key: 'batchStreamRequest',
            verbInfo: 'Post',
            isLatest: false,
            id: (requests.length + 1).toString(),
            correlationId: '',
            verb: 'post',
            body: message.data,
            url: 'batchStreamRequest',
            sentTime: message.timestamp,
        });
    }
}

function extractRpcChannels(
    requests: RequestInfo[],
    filter: RequestFilter,
    state?: ContactSupportRequestState
) {
    if (!state?.streams?.channels) {
        return;
    }
    for (const channel of getChannels(state, filter)) {
        for (
            let requestIndex = 0;
            requestIndex < channel.latestRequests.length;
            requestIndex++
        ) {
            const request = channel.latestRequests[requestIndex];
            const { destination, payload, sentTime } = request;

            const requestInfo: RequestInfo = {
                key: destination as string,
                id: (requests.length + 1).toString(),
                correlationId: request.correlationId,
                verbInfo: 'channel',
                verb: 'get',
                body: payload,
                sentTime: sentTime,
                url: 'n/a',
                isLatest: false,
            };

            const response = request.latestResponses[0];
            if (response) {
                requestInfo.response = {
                    data: response.payload,
                    receivedTime: response.receivedTime,
                    status: 200,
                };
            }

            requests.push(requestInfo);
        }
    }
}

function calcIsLatest(orderedRequests: RequestInfo[]) {
    const keys: string[] = [];
    for (const r of orderedRequests) {
        if (keys.indexOf(r.key) === -1) {
            r.isLatest = true;
            keys.push(r.key);
        }
    }
}
